
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { TimelineConfig } from '../core/list-layout.types';
import { getNestedProperty } from '../core/list-layout.utils';
import moment from 'moment';

interface TimelineRow {
    id: string;
    label: string;
    item: any;
    segments: TimelineSegment[];
    offsetY: number;
    index: number;
}

interface TimelineSegment {
    id: string;
    start: Date;
    end: Date;
    type: string;
    item: any;
}

interface TimeAxisTick {
    value: number;
    x: number;
    label: string;
    isMajor: boolean;
}

@Component
export default class ListLayoutTimeline extends Vue {
    @Prop({ required: true }) config!: TimelineConfig;
    @Prop({ type: Array, default: () => [] }) items!: any[];
    @Prop({ type: Boolean, default: false }) loading!: boolean;

    zoomLevel = 1;
    timeScale: 'day' | 'week' | 'month' = 'day';
    scrollTop = 0;
    scrollLeft = 0;
    containerWidth = 0;
    containerHeight = 0;
    visibleStartIndex = 0;
    visibleEndIndex = 0;

    segmentMargin = 4;
    pixelsPerDay = 50;
    timeStart: Date = new Date();
    timeEnd: Date = new Date();

    get totalHeight(): number {
        return this.items.length * this.config.rowHeight;
    }

    get totalWidth(): number {
        const days = moment(this.timeEnd).diff(moment(this.timeStart), 'days');
        return days * this.pixelsPerDay * this.zoomLevel;
    }

    get visibleRows(): TimelineRow[] {
        return this.items
            .slice(this.visibleStartIndex, this.visibleEndIndex)
            .map((item, idx) => this.createRow(item, this.visibleStartIndex + idx));
    }

    get timeAxisTicks(): TimeAxisTick[] {
        const ticks: TimeAxisTick[] = [];
        const start = moment(this.timeStart);
        const end = moment(this.timeEnd);
        const current = start.clone();

        while (current.isBefore(end)) {
            const x = this.timeToX(current.toDate());
            ticks.push({
                value: current.valueOf(),
                x,
                label: this.formatTickLabel(current),
                isMajor: this.isTickMajor(current),
            });

            switch (this.timeScale) {
                case 'day':
                    current.add(1, 'day');
                    break;
                case 'week':
                    current.add(1, 'week');
                    break;
                case 'month':
                    current.add(1, 'month');
                    break;
            }
        }

        return ticks;
    }

    get showCurrentTime(): boolean {
        const now = new Date();
        return now >= this.timeStart && now <= this.timeEnd;
    }

    get currentTimeX(): number {
        return this.timeToX(new Date());
    }

    mounted() {
        this.calculateTimeRange();
        this.updateVisibleRange();
        this.$nextTick(() => {
            this.measureContainer();
        });

        window.addEventListener('resize', this.handleResize);
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    }

    @Watch('items', { deep: true })
    onItemsChange() {
        this.calculateTimeRange();
        this.updateVisibleRange();
    }

    @Watch('zoomLevel')
    onZoomChange() {
        this.updateVisibleRange();
    }

    calculateTimeRange() {
        if (this.items.length === 0) {
            this.timeStart = moment().startOf('month').toDate();
            this.timeEnd = moment().endOf('month').toDate();
            return;
        }

        let minDate = new Date();
        let maxDate = new Date();

        this.items.forEach((item) => {
            const start = getNestedProperty(item, this.config.startDateProperty);
            const end = getNestedProperty(item, this.config.endDateProperty);

            if (start) {
                const startDate = new Date(start);
                if (!minDate || startDate < minDate) {
                    minDate = startDate;
                }
            }

            if (end) {
                const endDate = new Date(end);
                if (!maxDate || endDate > maxDate) {
                    maxDate = endDate;
                }
            }
        });

        this.timeStart = moment(minDate).subtract(7, 'days').startOf('day').toDate();
        this.timeEnd = moment(maxDate).add(7, 'days').endOf('day').toDate();
    }

    createRow(item: any, index: number): TimelineRow {
        const label = this.config.labelProperty ? getNestedProperty(item, this.config.labelProperty) : `Item ${index + 1}`;

        const start = getNestedProperty(item, this.config.startDateProperty);
        const end = getNestedProperty(item, this.config.endDateProperty);

        const segments: TimelineSegment[] = [];
        if (start && end) {
            segments.push({
                id: `seg-${index}`,
                start: new Date(start),
                end: new Date(end),
                type: 'default',
                item,
            });
        }

        return {
            id: `row-${index}`,
            label,
            item,
            segments,
            offsetY: index * this.config.rowHeight,
            index,
        };
    }

    timeToX(date: Date): number {
        const diffDays = moment(date).diff(moment(this.timeStart), 'days', true);
        return diffDays * this.pixelsPerDay * this.zoomLevel;
    }

    getSegmentWidth(segment: TimelineSegment): number {
        const diffDays = moment(segment.end).diff(moment(segment.start), 'days', true);
        const width = diffDays * this.pixelsPerDay * this.zoomLevel;
        return Math.max(width, 2);
    }

    getSegmentColor(type: string): string {
        const segmentType = this.config.segmentTypes.find((t) => t.type === type);
        return segmentType?.color || '#2196f3';
    }

    formatTickLabel(date: moment.Moment): string {
        switch (this.timeScale) {
            case 'day':
                return date.format('DD.MM');
            case 'week':
                return date.format('WW');
            case 'month':
                return date.format('MMM YY');
            default:
                return date.format('DD.MM');
        }
    }

    isTickMajor(date: moment.Moment): boolean {
        switch (this.timeScale) {
            case 'day':
                return date.date() === 1;
            case 'week':
                return date.week() % 4 === 0;
            case 'month':
                return date.month() === 0;
            default:
                return false;
        }
    }

    updateVisibleRange() {
        const bufferSize = 5;
        const visibleCount = Math.ceil(this.containerHeight / this.config.rowHeight) + bufferSize * 2;
        this.visibleStartIndex = Math.max(0, Math.floor(this.scrollTop / this.config.rowHeight) - bufferSize);
        this.visibleEndIndex = Math.min(this.items.length, this.visibleStartIndex + visibleCount);
    }

    handleScroll(event: Event) {
        const target = event.target as HTMLElement;
        this.scrollTop = target.scrollTop;
        this.scrollLeft = target.scrollLeft;
        this.updateVisibleRange();

        // Sync other scroll areas
        const timeAxis = this.$refs.timeAxis as HTMLElement;
        const rowLabels = this.$refs.rowLabels as HTMLElement;

        if (timeAxis) timeAxis.scrollLeft = this.scrollLeft;
        if (rowLabels) rowLabels.scrollTop = this.scrollTop;

        // Check for load more
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;
        if (scrollHeight - this.scrollTop - clientHeight < 200) {
            this.$emit('load-more');
        }
    }

    syncHorizontalScroll(event: Event) {
        const target = event.target as HTMLElement;
        const chart = this.$refs.timelineChart as HTMLElement;
        if (chart) {
            chart.scrollLeft = target.scrollLeft;
        }
    }

    syncVerticalScroll(event: Event) {
        const target = event.target as HTMLElement;
        const chart = this.$refs.timelineChart as HTMLElement;
        if (chart) {
            chart.scrollTop = target.scrollTop;
        }
    }

    handleSegmentClick(segment: TimelineSegment) {
        this.$emit('item-click', segment.item);
    }

    measureContainer() {
        const container = this.$refs.container as HTMLElement;
        if (container) {
            this.containerWidth = container.clientWidth;
            this.containerHeight = container.clientHeight;
            this.updateVisibleRange();
        }
    }

    handleResize() {
        this.measureContainer();
    }

    zoomIn() {
        this.zoomLevel = Math.min((this.config.maxZoom || 3), this.zoomLevel + 0.25);
    }

    zoomOut() {
        this.zoomLevel = Math.max((this.config.minZoom || 0.5), this.zoomLevel - 0.25);
    }
}
